import { useVoucherBookBackgroundTask } from "@nkr/models/voucher-book/voucher-book-background-task";
import { useDownloadApi } from "@nkr/utils/rest-api";
import * as moment from "moment";
import { useEffect, useRef, useState } from "react";

const POLL_INTERVAL_MS  = 30000;
const MAX_POLLS         = 60;

export const VoucherBookGenerate = () => {
    const api           = useVoucherBookBackgroundTask();
    const statusResult  = api.useStatus();
    const infoResult    = api.useInfo();
    const starter       = api.initiate;

    const download = useDownloadApi(`${__API__}/admin/voucher-book`, {
        fileName : "voucher-book.pdf",
        mimeType : "application/pdf"
    });

    const status    = statusResult?.payload;
    const info      = infoResult?.payload;

    const hasJob    = status?.hasJob === true;
    const percent   = status?.percentValue;
    const isRunning  = hasJob && percent != null && percent < 100;
    const isComplete = hasJob && percent != null && percent >= 100;

    const [isMonitoring, setIsMonitoring] = useState(false);

    const runDate = status?.lastRunDateTime ? moment.utc(status.lastRunDateTime).local().format("MM/DD/YYYY @ h:mmA") : "N/A";
    const genDate = info?.generatedDateTime ? moment.utc(info.generatedDateTime).local().format("MM/DD/YYYY @ h:mmA") : "Never generated";

    // Keep a ref to the latest api so the effects don't need `api` in their deps
    const apiRef = useRef(api);
    apiRef.current = api;

    const generateVoucherBook = async () => {
        if( confirm("Are you sure you want to regenerate the voucher book? This replaces the current book and takes several minutes.") ) {
            await starter({id:0});
            // pulls the freshly enqueued job back, which is what arms the monitor below
            await apiRef.current.refreshStatus?.();
        }
    }

    // Only an unfinished run arms the monitor. A page opened on a job that never ran, or on one
    // already at 100%, never polls -- there is nothing left to watch.
    useEffect(() => {
        if (isRunning) {
            setIsMonitoring(true);
        }
    }, [isRunning]);

    // Crossing 100% while under watch is the one event that justifies going back to S3.
    useEffect(() => {
        if (!isMonitoring || !isComplete) return;

        setIsMonitoring(false);
        apiRef.current.refreshInfo?.();
    }, [isMonitoring, isComplete]);

    useEffect(() => {
        if (!isMonitoring) return;

        let timer: number | null = null;
        let stopped = false;
        let polls   = 0;

        const poll = async () => {
            polls++;

            try {
                await apiRef.current.refreshStatus?.();
            } catch {
                // a failed request ends the loop instead of retrying every 30s forever;
                // the fetch layer has already toasted the error
                return;
            }

            // a job that dies mid-run leaves the percentage short of 100 forever, so the
            // watch is capped rather than left to poll for the life of the tab
            if (stopped || polls >= MAX_POLLS) return;

            timer = window.setTimeout(poll, POLL_INTERVAL_MS);
        };

        timer = window.setTimeout(poll, POLL_INTERVAL_MS);

        return () => {
            stopped = true;
            if (timer) clearTimeout(timer);
        };
    }, [isMonitoring]);

    return (
        <div className="well well-sm">
            <div className="row">
                <div className="col-xs-12">
                    <table>
                        <tbody>
                            <tr>
                                <td style={{ width: 150, verticalAlign: "middle" }} rowSpan={2}>
                                    <button className="btn btn-primary" onClick={generateVoucherBook}>Generate</button>
                                </td>
                                <td style={{ width: 150 }}><strong>Last Generated:</strong></td>
                                <td style={{ width: "auto" }}>{genDate}</td>
                            </tr>
                            <tr>
                                <td style={{ width: 150 }}><strong>Last Run Started:</strong></td>
                                <td style={{ width: "auto" }}>{runDate}</td>
                            </tr>
                            <tr>
                                <td style={{ width: 150, verticalAlign: "middle" }}>
                                    <button
                                        className="btn btn-default"
                                        disabled={!info?.isAvailable || download.isDownloading}
                                        onClick={() => download.downloadFile()}
                                    >
                                        Download
                                    </button>
                                </td>
                                <td style={{ width: 150 }}><strong>Status: <span className="label label-default">{hasJob ? status.percentComplete : "N/A"}</span></strong></td>
                                <td style={{ width: "auto" }}><span className="label label-info">{hasJob ? status.lastRunStatus : "N/A"}</span></td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};
